// Economy: supply accounting, worker harvesting, construction and production
// queues.
//
// Owns: carrying, gatherNode, gatherTimer, gatherState, dropoffId, buildTargetId,
// building.queue, building.buildProgress/constructing, node.miner/node.gatherers,
// player.minerals/gas/supply.
// Writes e.moveTarget for workers executing gather / build / repair orders.
//
// THE HAUL (the rule the whole mineral curve hangs off)
// ----------------------------------------------------
// A worker mines from the OUTWARD side of its patch -- the side away from the
// town hall it delivers to -- and delivers in genuine contact with the hall's
// footprint. It is not a cosmetic choice; it is what makes a mineral patch
// behave the way a StarCraft player expects.
//
// A patch serialises on one miner, so
//     workers_per_patch_at_saturation = 1 + travel_time / mine_time
// which means the whole saturation curve is decided by the ratio of the round
// trip to MINE_DURATION. BW saturates a patch at ~2 workers, so the round trip
// has to be about as long as the 3.0 s mine. On this map a town hall footprint
// and its nearest patch footprint are only 2.8 tiles apart on the diagonal; if
// a worker mines from the near edge and drops off at the nearest hall corner it
// shuttles 1.3 tiles, the round trip costs 0.4 s, and eight patches saturate at
// nine workers instead of sixteen. Mining from the far side turns the same
// geometry into a ~6.7 tile haul (a ~2.7 s round trip), which lands the curve
// on ~52 minerals per worker per minute, 91% of linear at 12 workers and 85% at
// 16 -- i.e. a patch absorbing very nearly two workers, as BW does.
//
// Where a patch is so close to the hall that even its far side is a short walk,
// the stand point is pushed a little further out (HAUL_STANDOFF_MAX caps how
// far). That is a workaround for this map's arcs, not the design; see the
// note in README / coreChangeRequests about moving the tightest offsets out.
//
// GEOMETRY RULE: every interaction range in here is measured with edgeDist()
// against the target's FOOTPRINT, never centre-to-centre. A single radial stop
// distance cannot work against a square: for a 4x4 building "touching the north
// face" is 2.0 tiles from the centre and "touching a corner" is 2.83, so a
// radius that reaches the corner puts the unit *inside* the building on the
// axis. That is how workers used to end up wedged inside a structure.
//
// Long hauls steer at a point shared by every worker on the node, because the
// flow-field cache is keyed on the goal tile and a per-worker goal that slides
// every tick costs a full-map Dijkstra per worker per frame.

import { UnitDefs, BuildingDefs } from './defs.js';
import {
  TICK_DT, MAX_SUPPLY, MINERALS_PER_TRIP, GAS_PER_TRIP,
  MINE_DURATION, GAS_DURATION, GAS_WORKER_CAP,
} from './constants.js';
import { setOrder, nextOrder, refund } from './commands.js';
import { edgeDist, contactRadius } from './world.js';

// --- interaction tuning ---------------------------------------------------
// Pads are added to the unit radius and measured from the target FOOTPRINT.
const MINE_PAD       = 0.40;  // gap left between worker and patch while mining
const MINE_REACH     = 1.30;  // how close to the mine point counts as "on it"
const MINE_OUT_SLOP  = 0.30;  // ...but no further OUT along the haul line than this
const QUEUE_SIDE     = 1.15;  // a worker awaiting its turn parks abreast, not behind
const DROPOFF_PAD    = 0.30;  // gap left between worker and town hall on delivery
const DROPOFF_REACH  = 0.60;  // cargo is handed over inside radius + this
const BUILD_PAD      = 0.40;  // stand-off while placing / building / repairing
const BUILD_REACH    = 0.90;  // close enough to pour concrete / repair
const BUILD_STALL_TICKS = 720; // 30 s of zero progress toward a site = give up, refund

const NEAR_RANGE     = 1.60;  // steer at a per-worker face point only inside this
const MINE_SEARCH    = 16;    // how far from the drop-off we look for a patch
const CROWD_PENALTY  = 4.0;   // tiles of "distance" charged per worker already on a patch

// The haul the tuning wants: long enough that the round trip costs about one
// MINE_DURATION, which is what puts patch saturation at ~2 workers. Real paths
// detour around the patch itself, so aim a little short of the exact figure.
// A node whose far side is already this far from the hall is left alone; a node
// tucked right up against the hall is worked from a little further out, but
// never more than HAUL_STANDOFF_MAX beyond its own edge -- past that the worker
// stops looking like it is mining anything.
const HAUL_SLACK       = 0.95;
const HAUL_STANDOFF_MAX = 1.60;
const HAUL_STEP        = 0.20;

const _pt = { x: 0, y: 0 };
const _mp = { x: 0, y: 0, ux: 1, uy: 0 };
const _near = [];

// Build orders are paid for when the command is issued, so an order that never
// reaches placement (worker killed, player re-ordered the worker) has to give
// the money back. Keyed by world so nothing is bolted onto the world object,
// and keyed inside by the ORDER object so a worker holding two queued build
// orders cannot lose track of the first one's money.
const _paidBuilds = new WeakMap();
function paidBuilds(world) {
  let m = _paidBuilds.get(world);
  if (!m) { m = new Map(); _paidBuilds.set(world, m); }
  return m;
}

export function economySystem(world) {
  recomputeSupply(world);
  reconcileNodes(world);
  sweepPaidBuilds(world);
  for (let p = 0; p < 2; p++) {
    for (const b of world.buildingsOf(p)) tickBuilding(world, b);
    for (const u of world.unitsOf(p)) tickWorker(world, u);
  }
}

function recomputeSupply(world) {
  for (let p = 0; p < 2; p++) {
    const pl = world.players[p];
    let used = 0, max = 0;
    for (const id of world.byPlayer[p]) {
      const e = world.entities.get(id);
      if (!e || !e.alive) continue;
      if (e.kind === 'unit') used += UnitDefs[e.defId].supply;
      else if (e.kind === 'building' && !e.constructing) max += BuildingDefs[e.defId].supplyProvided || 0;
    }
    pl.supplyUsed = used;
    pl.supplyMax = Math.min(MAX_SUPPLY, max);
  }
}

// ---- node bookkeeping ----------------------------------------------------
/**
 * Derive every harvest-node counter from scratch each tick instead of trying to
 * keep incremental counters honest. A worker can leave the mineral line by
 * dying, by being re-ordered, or by having its patch mined out from under it;
 * every one of those paths used to be able to leak a `gatherers` slot or leave
 * `node.miner` pointing at a worker that had wandered off, which silently
 * locked a refinery for the rest of the game. Recomputing is ~100 operations a
 * tick and makes the invariant unbreakable.
 */
function reconcileNodes(world) {
  const nodes = [];
  for (const id of world.resources) { const r = world.get(id); if (r) { r.gatherers = 0; nodes.push(r); } }
  for (let p = 0; p < 2; p++) {
    for (const b of world.buildingsOf(p)) {
      if (b.defId !== 'refinery') continue;
      b.gatherers = 0;
      nodes.push(b);
    }
  }

  for (let p = 0; p < 2; p++) {
    for (const u of world.unitsOf(p)) {
      if (u.gatherNode == null) continue;
      const n = world.alive(u.gatherNode);
      if (!n) {                                  // patch mined out / refinery lost
        u.gatherNode = null;
        u.gatherTimer = 0;
        if (u.gatherState === 'mining') u.gatherState = u.carrying ? 'to_dropoff' : 'to_node';
        continue;
      }
      n.gatherers++;
    }
  }

  for (const n of nodes) {
    if (n.defId === 'refinery') n.gasWorkers = n.gatherers;
    if (n.miner == null) continue;
    const m = world.alive(n.miner);
    // the slot is held only by a live worker that is actually mining THIS node
    if (!m || m.gatherNode !== n.id || m.gatherTimer <= 0) n.miner = null;
  }

  // A refinery that was destroyed or cancelled hands the geyser back: restore
  // the footprint it borrowed so the tile is not silently walkable and the
  // geyser can be re-taken.
  for (const id of world.resources) {
    const g = world.get(id);
    if (!g || g.defId !== 'geyser' || g.refineryId == null) continue;
    if (world.alive(g.refineryId)) continue;
    g.refineryId = null;
    world.grid.stamp(g.tx, g.ty, g.w, g.h, g.id);
    world.grid.markNoBuild(g.tx, g.ty, g.w, g.h, 1);
  }
}

export function releaseNode(world, u) {
  if (u.gatherNode == null) return;
  const n = world.get(u.gatherNode);
  if (n && n.miner === u.id) n.miner = null;
  if (n && n.gatherers) n.gatherers--;
  u.gatherNode = null;
  u.gatherTimer = 0;
  if (u.gatherState === 'mining') u.gatherState = u.carrying ? 'to_dropoff' : 'to_node';
}

function attachNode(world, u, node) {
  if (u.gatherNode === node.id) return;
  releaseNode(world, u);
  u.gatherNode = node.id;
  node.gatherers = (node.gatherers || 0) + 1;
  u.gatherTimer = 0;                    // never carry a half-finished mine across nodes
  if (!u.carrying) u.gatherState = 'to_node';
}

// ---- geometry ------------------------------------------------------------
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/**
 * A point `pad` tiles outside `e`, on the side the unit is already on.
 * For footprints this is the closest point on the rectangle pushed straight
 * out; for circles it is the obvious radial offset. The result always has
 * edgeDist(e, p) === pad, from every approach angle.
 */
function faceApproach(e, u, pad, out) {
  if (e.tx === undefined) {
    let dx = u.x - e.x, dy = u.y - e.y;
    let d = Math.hypot(dx, dy);
    if (d < 1e-4) { dx = 1; dy = 0; d = 1; }
    out.x = e.x + (dx / d) * ((e.radius || 0) + pad);
    out.y = e.y + (dy / d) * ((e.radius || 0) + pad);
    return out;
  }
  const x0 = e.tx, x1 = e.tx + e.w, y0 = e.ty, y1 = e.ty + e.h;
  if (u.x > x0 && u.x < x1 && u.y > y0 && u.y < y1) {
    // standing inside the footprint: leave through the nearest face
    const dl = u.x - x0, dr = x1 - u.x, dt = u.y - y0, db = y1 - u.y;
    const m = Math.min(dl, dr, dt, db);
    if (m === dl) { out.x = x0 - pad; out.y = u.y; }
    else if (m === dr) { out.x = x1 + pad; out.y = u.y; }
    else if (m === dt) { out.x = u.x; out.y = y0 - pad; }
    else { out.x = u.x; out.y = y1 + pad; }
    return out;
  }
  const nx = clamp(u.x, x0, x1), ny = clamp(u.y, y0, y1);
  let dx = u.x - nx, dy = u.y - ny;
  const d = Math.hypot(dx, dy) || 1;
  out.x = nx + (dx / d) * pad;
  out.y = ny + (dy / d) * pad;
  return out;
}

/** Distance from e's centre to its own edge along the unit vector (ux,uy). */
function exitDist(e, ux, uy) {
  if (e.tx === undefined) return e.radius || 0;
  const hx = e.w / 2, hy = e.h / 2;
  const ax = ux < 0 ? -ux : ux, ay = uy < 0 ? -uy : uy;
  let t = Infinity;
  if (ax > 1e-9) t = hx / ax;
  if (ay > 1e-9) { const ty = hy / ay; if (ty < t) t = ty; }
  return t === Infinity ? (hx > hy ? hx : hy) : t;
}

/**
 * moveTarget that walks `u` to a stand position `pad` outside `e`.
 *
 * The face point is per-worker, so it is only safe to hand to movement once the
 * worker is inside movement's direct-steering radius. Beyond that movement
 * routes through the flow-field cache, which is keyed on the goal TILE: a goal
 * that slides a little every tick misses the cache every tick and costs a
 * full-map Dijkstra per worker per frame. Long hauls therefore aim at the
 * target centre, which every worker bound for that target shares.
 */
function approachTarget(world, u, e, pad) {
  faceApproach(e, u, u.radius + pad, _pt);
  const dx = _pt.x - u.x, dy = _pt.y - u.y;
  if (dx * dx + dy * dy < NEAR_RANGE * NEAR_RANGE) return { x: _pt.x, y: _pt.y, stopDist: 0.05 };
  return { x: e.x, y: e.y, stopDist: contactRadius(e, u.radius) };
}

/**
 * Where a worker stands to work `node`: on the far side from `home`, so the
 * trip back to the town hall is a real haul. Shared by every worker on the
 * node, so they all steer at one flow-field goal.
 *
 * Writes { x, y, ux, uy } into `out`; (ux,uy) is the outward unit vector, which
 * the caller uses to park queued workers abreast of the miner and to reject a
 * clamp-on from further out than the stand point.
 */
function minePoint(world, node, home, u, out) {
  let ux, uy;
  if (home) { ux = node.x - home.x; uy = node.y - home.y; }
  else { ux = u.x - node.x; uy = u.y - node.y; }
  let d = Math.hypot(ux, uy);
  if (d < 1e-4) { ux = 1; uy = 0; d = 1; }
  ux /= d; uy /= d;
  out.ux = ux; out.uy = uy;
  const base = exitDist(node, ux, uy) + u.radius + MINE_PAD;
  const want = home ? u.speed * MINE_DURATION * 0.5 * HAUL_SLACK : 0;
  // The far side is open ground for every node on this map, but a future map
  // could back a patch onto a cliff; step outward, then give up and take the
  // nearest legal contact point rather than sending the worker somewhere it
  // can never stand.
  let found = -1;
  for (let extra = 0; extra <= HAUL_STANDOFF_MAX + 1e-9; extra += HAUL_STEP) {
    const r = base + extra;
    const x = node.x + ux * r, y = node.y + uy * r;
    if (!fits(world, x, y, u.radius)) continue;
    out.x = x; out.y = y; found = r;
    if (!home || edgeDist(home, x, y) >= want) return out;
  }
  if (found >= 0) return out;                  // best legal stand-off we could find
  for (let k = 1; k <= 5; k++) {
    const r = base + HAUL_STANDOFF_MAX + k * 0.5;
    const x = node.x + ux * r, y = node.y + uy * r;
    if (fits(world, x, y, u.radius)) { out.x = x; out.y = y; return out; }
  }
  faceApproach(node, u, u.radius + MINE_PAD, out);
  if (!fits(world, out.x, out.y, u.radius)) {
    const t = world.grid.nearestPassable(out.x, out.y);
    out.x = t.x + 0.5; out.y = t.y + 0.5;
  }
  return out;
}

// ---- production ----------------------------------------------------------
function tickBuilding(world, b) {
  if (b.constructing) return;                 // progress is driven by the builder
  if (!b.queue.length) return;
  const item = b.queue[0];
  const pl = world.players[b.owner];

  if (item.timeLeft > 0) { item.timeLeft = Math.max(0, item.timeLeft - TICK_DT); return; }

  if (item.kind === 'unit') {
    const def = UnitDefs[item.defId];
    // finished but supply-blocked: the unit waits at the door, like BW, and
    // pops the instant a depot finishes or something dies
    if (pl.supplyUsed + def.supply > pl.supplyMax) { pl.stats.supplyBlockedTicks++; return; }
    const spot = spawnSpot(world, b, def);
    const u = world.spawnUnit(b.owner, item.defId, spot.x, spot.y);
    // keep supply honest within the tick: two barracks finishing on the same
    // tick with one supply free must not both pop
    pl.supplyUsed += def.supply;
    b.queue.shift();
    applyRally(world, b, u);
    world.emit({ type: 'trained', id: u.id, owner: b.owner, defId: item.defId, from: b.id });
  } else {
    pl.tech.add(item.defId);
    b.queue.shift();
    world.emit({ type: 'researched', owner: b.owner, defId: item.defId });
  }
}

/** Where a finished unit steps out. Biased toward the rally point, like BW. */
function spawnSpot(world, b, def) {
  let baseAng;
  const rp = b.rally;
  if (rp) {
    const t = rp.targetId != null ? world.get(rp.targetId) : null;
    const tx = t ? t.x : rp.x, ty = t ? t.y : rp.y;
    baseAng = Math.atan2(ty - b.y, tx - b.x);
  } else {
    baseAng = b.owner === 0 ? Math.PI * 0.25 : Math.PI * 1.25;
  }
  const r0 = Math.max(b.w, b.h) / 2 + (def.radius || 0.35) + 0.35;
  const STEPS = 16, ARC = (Math.PI * 2) / STEPS;
  for (let ring = 0; ring < 8; ring++) {
    const rad = r0 + ring * 0.6;
    for (let i = 0; i < STEPS; i++) {
      const a = baseAng + (((i + 1) >> 1) * ARC) * (i & 1 ? 1 : -1);
      const x = b.x + Math.cos(a) * rad, y = b.y + Math.sin(a) * rad;
      if (def.flying || fits(world, x, y, def.radius || 0.35)) return { x, y };
    }
  }
  return { x: b.x, y: b.y + r0 };
}

/** Same footprint test movement uses, so a spawned unit is never born stuck. */
function fits(world, x, y, r) {
  const g = world.grid;
  const p = r * 0.72;
  const x0 = Math.floor(x - p), x1 = Math.floor(x + p);
  const y0 = Math.floor(y - p), y1 = Math.floor(y + p);
  for (let ty = y0; ty <= y1; ty++)
    for (let tx = x0; tx <= x1; tx++)
      if (!g.passable(tx, ty)) return false;
  return true;
}

/**
 * Apply a production building's rally point to a unit that just popped.
 * Three cases, exactly as in BW: a bare point is a move order, a resource (or
 * your own refinery) puts a worker straight onto it, and an enemy is an attack.
 */
function applyRally(world, b, u) {
  const isWorker = !!UnitDefs[u.defId].isWorker;
  const rp = b.rally;
  if (!rp) {
    // no rally: workers walk themselves back to the mineral line
    if (isWorker) setOrder(u, { type: 'gather', targetId: null }, false);
    return;
  }
  const tgt = rp.targetId != null ? world.alive(rp.targetId) : null;
  if (tgt) {
    const harvestable = tgt.kind === 'resource'
      || (tgt.kind === 'building' && tgt.owner === u.owner && tgt.defId === 'refinery');
    if (isWorker && harvestable) { setOrder(u, { type: 'gather', targetId: tgt.id }, false); return; }
    if (tgt.owner >= 0 && tgt.owner !== u.owner) {
      // Seed the last-known position from where the player actually clicked.
      // Without it combat.js drops the order outright the moment the target is
      // sitting in fog, and rallying onto an enemy does nothing at all. Seeding
      // from the target's LIVE position instead would be a wallhack.
      setOrder(u, { type: 'attack', targetId: tgt.id, lastX: rp.x, lastY: rp.y }, false);
      return;
    }
    setOrder(u, { type: 'move', x: tgt.x, y: tgt.y }, false);
    return;
  }
  setOrder(u, { type: 'move', x: rp.x, y: rp.y }, false);
}

// ---- worker dispatch -----------------------------------------------------
function tickWorker(world, u) {
  if (!UnitDefs[u.defId].isWorker) return;
  // Shift-queued build orders are paid for the moment they are issued, long
  // before the worker gets to them. Register them now or a queue that gets
  // wiped by a fresh order silently eats the money.
  for (let i = 0; i < u.orderQueue.length; i++) {
    const q = u.orderQueue[i];
    if (q.type === 'build' && q.paid) trackPaidBuild(world, u, q);
  }
  const o = u.order;
  if (!o) { idleWorker(world, u); return; }
  switch (o.type) {
    case 'gather': tickGather(world, u, o); break;
    case 'build':  tickBuild(world, u, o); break;
    case 'repair': tickRepair(world, u, o); break;
    default:
      // any other order takes the worker off the line; free the slot NOW so a
      // refinery is not held hostage by a worker that walked off to build
      if (u.gatherNode != null) releaseNode(world, u);
      break;
  }
}

function idleWorker(world, u) {
  // an idle worker holding cargo, or one still assigned to a patch, goes back
  // to work rather than standing around with 5 minerals in its hands
  if (u.carrying || u.gatherNode != null) setOrder(u, { type: 'gather', targetId: u.gatherNode }, false);
}

// ---- harvesting ----------------------------------------------------------
/** Nearest completed town hall of `owner` to the point (x,y). */
function nearestHall(world, owner, x, y) {
  let best = null, bd = Infinity;
  for (const b of world.buildingsOf(owner)) {
    if (b.constructing || !BuildingDefs[b.defId].isTownHall) continue;
    const d = Math.hypot(b.x - x, b.y - y);
    if (d < bd) { bd = d; best = b; }
  }
  return best;
}

/** Resolve an order target to the thing a worker actually harvests from. */
function resolveNode(world, id) {
  const e = id == null ? null : world.alive(id);
  if (!e) return null;
  if (e.kind === 'resource') {
    if (e.defId === 'geyser') {
      const ref = e.refineryId != null ? world.alive(e.refineryId) : null;
      return ref && !ref.constructing ? ref : null;
    }
    return e.amount > 0 ? e : null;
  }
  if (e.kind === 'building' && e.defId === 'refinery' && !e.constructing) return e;
  return null;
}

/** Least-contested live patch near the worker's own base. */
function pickNode(world, u) {
  const home = nearestHall(world, u.owner, u.x, u.y);
  const ox = home ? home.x : u.x, oy = home ? home.y : u.y;
  let best = null, bs = Infinity;
  let far = null, fd = Infinity;
  for (const id of world.resources) {
    const r = world.get(id);
    if (!r || !r.alive || r.defId !== 'mineral' || r.amount <= 0) continue;
    const d = Math.hypot(r.x - ox, r.y - oy);
    if (d < fd) { fd = d; far = r; }
    if (d > MINE_SEARCH) continue;
    const score = d + (r.gatherers || 0) * CROWD_PENALTY;
    if (score < bs) { bs = score; best = r; }
  }
  return best || far;                          // mined out at home? go find one
}

function tickGather(world, u, o) {
  // 1. what are we working?
  let node = resolveNode(world, o.targetId);
  if (node && node.kind === 'building' && u.gatherNode !== node.id
      && (node.gatherers || 0) >= GAS_WORKER_CAP) {
    node = null;                               // refinery full: back to minerals
  }
  if (!node) {
    node = pickNode(world, u);
    if (!node) {
      // nothing left to mine anywhere: still walk the cargo home
      releaseNode(world, u);
      o.targetId = null;
      if (u.carrying) { deliver(world, u, nearestHall(world, u.owner, u.x, u.y)); return; }
      u.moveTarget = null;
      return;
    }
    o.targetId = node.id;
  }
  attachNode(world, u, node);

  // The hall this node belongs to. Derived from the NODE, not the worker, so
  // every worker on a patch shares one haul line and one flow-field goal.
  const home = nearestHall(world, u.owner, node.x, node.y);
  if (home) u.dropoffId = home.id;

  // 2. cargo goes home first; a successful drop falls straight through to the
  //    walk back, so no tick is wasted standing at the town hall
  if (u.carrying) { if (!deliver(world, u, home)) return; }

  // 3. and a completed mine falls straight through to the walk home, so no
  //    tick is wasted standing on the patch either
  if (mine(world, u, node, home)) deliver(world, u, home);
}

/** Returns true when the cargo was handed over this tick. */
function deliver(world, u, home) {
  if (!home || home.owner !== u.owner || home.constructing) {
    home = nearestHall(world, u.owner, u.x, u.y);
  }
  if (!home) { u.moveTarget = null; u.gatherState = 'to_dropoff'; return false; }
  u.dropoffId = home.id;

  if (edgeDist(home, u.x, u.y) > u.radius + DROPOFF_REACH) {
    u.gatherState = 'to_dropoff';
    u.moveTarget = approachTarget(world, u, home, DROPOFF_PAD);
    return false;
  }
  const pl = world.players[u.owner];
  const amt = u.carrying.amount;
  if (u.carrying.type === 'mineral') { pl.minerals += amt; pl.stats.mineralsMined += amt; }
  else { pl.gas += amt; pl.stats.gasMined += amt; }
  world.emit({ type: 'deliver', owner: u.owner, res: u.carrying.type, amount: amt, x: u.x, y: u.y });
  u.carrying = null;
  u.gatherState = 'to_node';
  u.moveTarget = null;
  return true;
}

/**
 * Work the node. Returns true on the tick cargo is picked up, so the caller can
 * start the walk home in the same tick.
 *
 * A node serialises: `node.miner` is the one worker clamped onto it. Everybody
 * else queues one step further out along the same outward ray and steps in the
 * moment the slot frees. That single rule is what produces the BW saturation
 * curve -- and what caps a refinery at 100 gas/min no matter how many workers
 * are shoved onto it.
 */
function mine(world, u, node, home) {
  const isGas = node.kind === 'building';

  if (u.gatherTimer > 0) {
    u.gatherState = 'mining';
    u.moveTarget = null;
    u.gatherTimer -= TICK_DT;
    if (u.gatherTimer > 0) return false;
    u.gatherTimer = 0;
    if (node.miner === u.id) node.miner = null;
    const src = isGas ? world.alive(node.geyserId) : node;
    const per = isGas ? GAS_PER_TRIP : MINERALS_PER_TRIP;
    const take = src ? Math.min(per, src.amount) : 0;
    if (take > 0) {
      src.amount -= take;
      u.carrying = { type: isGas ? 'gas' : 'mineral', amount: take };
      u.gatherState = 'to_dropoff';
      return true;
    }
    u.carrying = null;
    u.gatherState = 'to_node';
    releaseNode(world, u);                     // exhausted: re-target next tick
    return false;
  }

  const mp = minePoint(world, node, home, u, _mp);
  const busy = node.miner != null && node.miner !== u.id && world.alive(node.miner);

  // Close enough to work it? Sideways slop is generous -- a worker shouldered
  // half a tile along the face is still on the patch -- but drifting OUTWARD
  // past the stand point is not allowed, or the haul silently shortens itself
  // and the worker ends up mining from three tiles away.
  const ox = u.x - mp.x, oy = u.y - mp.y;
  if (!busy && ox * ox + oy * oy <= MINE_REACH * MINE_REACH
      && ox * mp.ux + oy * mp.uy <= MINE_OUT_SLOP) {
    node.miner = u.id;
    u.gatherTimer = isGas ? GAS_DURATION : MINE_DURATION;
    u.gatherState = 'mining';
    u.moveTarget = null;
    return false;
  }

  // Walk in, or hold station BESIDE the node until our turn comes round --
  // never wander off to another patch, which is what made workers thrash.
  // Queued workers park abreast of the miner rather than behind it: that is
  // what a BW mineral line looks like, it keeps them out of the miner's way,
  // and it means the hand-off costs no walking at all when the slot frees.
  let tx = mp.x, ty = mp.y;
  if (busy) {
    const side = (u.id & 1) ? 1 : -1;
    const qx = mp.x - mp.uy * QUEUE_SIDE * side, qy = mp.y + mp.ux * QUEUE_SIDE * side;
    if (fits(world, qx, qy, u.radius)) { tx = qx; ty = qy; }
    else {
      const bx = mp.x + mp.uy * QUEUE_SIDE * side, by = mp.y - mp.ux * QUEUE_SIDE * side;
      if (fits(world, bx, by, u.radius)) { tx = bx; ty = by; }
    }
  }
  u.gatherState = 'to_node';
  u.moveTarget = { x: tx, y: ty, stopDist: 0.05 };
  return false;
}

// ---- construction --------------------------------------------------------
/**
 * Build orders are paid for at command time. Anything that stops the order from
 * ever reaching placement -- the worker dies on the way, the player re-orders
 * it, the order is dropped from the queue -- has to hand the money back.
 */
function sweepPaidBuilds(world) {
  const m = paidBuilds(world);
  if (!m.size) return;
  for (const [order, rec] of m) {
    if (order.buildingId != null || !order.paid) { m.delete(order); continue; }
    const u = world.alive(rec.uid);
    if (u && (u.order === order || u.orderQueue.indexOf(order) >= 0)) continue;
    order.paid = false;
    refund(world.players[rec.player], rec.cost);
    world.emit({ type: 'refund', player: rec.player, defId: order.defId });
    m.delete(order);
  }
}

function trackPaidBuild(world, u, o) {
  if (!o.paid) return;
  const m = paidBuilds(world);
  if (m.has(o)) return;
  m.set(o, { uid: u.id, cost: BuildingDefs[o.defId].cost, player: u.owner });
}

function tickBuild(world, u, o) {
  const def = BuildingDefs[o.defId];
  if (!def) { nextOrder(u); return; }

  // phase 1: walk to the site and place
  if (o.buildingId == null) {
    trackPaidBuild(world, u, o);
    const site = {
      tx: o.tx, ty: o.ty, w: def.size, h: def.size,
      x: o.tx + def.size / 2, y: o.ty + def.size / 2,
    };
    const d = edgeDist(site, u.x, u.y);
    if (d > u.radius + BUILD_REACH) {
      // Watchdog: a site behind a cliff, or one the worker can never squeeze
      // into, must not become a silent money sink with a worker shuffling at it
      // forever. Only zero net progress counts, so a long walk across the map
      // never trips it.
      if (o.bestD === undefined || d < o.bestD - 0.25) { o.bestD = d; o.stallTicks = 0; }
      else if (++o.stallTicks > BUILD_STALL_TICKS) {
        if (o.paid) { refund(world.players[u.owner], def.cost); o.paid = false; }
        paidBuilds(world).delete(o);
        world.emit({ type: 'error', player: u.owner, msg: `Cannot reach that ${def.name} site`, defId: o.defId, x: site.x, y: site.y });
        abandonBuild(world, u);
        return;
      }
      u.moveTarget = approachTarget(world, u, site, BUILD_PAD);
      return;
    }
    u.moveTarget = null;

    const geyser = o.geyserId != null ? world.alive(o.geyserId) : null;
    const legal = def.onGeyser
      ? !!(geyser && !geyser.refineryId)
      : world.grid.canPlace(o.tx, o.ty, def.size, def.size);
    if (!legal) {
      // Someone else took the spot while we walked. Give the money back and say
      // so -- silently eating 150 minerals is the worst possible outcome.
      if (o.paid) { refund(world.players[u.owner], def.cost); o.paid = false; }
      paidBuilds(world).delete(o);
      world.emit({ type: 'error', player: u.owner, msg: `Cannot build ${def.name} there`, defId: o.defId, x: site.x, y: site.y });
      abandonBuild(world, u);
      return;
    }

    if (def.onGeyser) world.grid.unstamp(geyser.tx, geyser.ty, geyser.w, geyser.h);
    const b = world.spawnBuilding(u.owner, o.defId, o.tx, o.ty, { complete: false, builderId: u.id });
    if (def.onGeyser) { b.geyserId = geyser.id; geyser.refineryId = b.id; }
    o.buildingId = b.id;
    o.paid = false;                            // the money is in the ground now
    paidBuilds(world).delete(o);
    u.buildTargetId = b.id;
    releaseNode(world, u);
    evictFromFootprint(world, b);              // nobody gets sealed inside it
  }

  // phase 2: pour concrete
  const b = world.alive(o.buildingId);
  // hp <= 0 means it was cancelled or shot down this tick and is about to be
  // reaped. Pouring concrete would heal it back above zero and the structure
  // would never actually die -- cancel would silently do nothing.
  if (!b || b.hp <= 0 || !b.constructing) { u.buildTargetId = null; finishBuild(world, u); return; }
  if (edgeDist(b, u.x, u.y) > u.radius + BUILD_REACH) {
    u.moveTarget = approachTarget(world, u, b, BUILD_PAD);
    return;
  }
  u.moveTarget = null;
  b.buildProgress = Math.min(1, b.buildProgress + TICK_DT / b.buildTime);
  b.hp = Math.max(b.hp, Math.floor(b.maxHp * (0.1 + 0.9 * b.buildProgress)));
  if (b.buildProgress >= 1) {
    b.constructing = false;
    b.hp = b.maxHp;
    b.builderId = null;
    // Credit the supply on THIS tick rather than waiting for next tick's
    // recompute: a unit held at the door by the supply cap has to pop the
    // instant the depot finishes, which is what BW does and what a player
    // watching the depot animation expects.
    const sp = BuildingDefs[b.defId].supplyProvided || 0;
    if (sp) { const pl = world.players[b.owner]; pl.supplyMax = Math.min(MAX_SUPPLY, pl.supplyMax + sp); }
    world.emit({ type: 'build_done', id: b.id, owner: b.owner, defId: b.defId, x: b.x, y: b.y });
    u.buildTargetId = null;
    finishBuild(world, u);
  }
}

/** Shove any ground unit standing inside a freshly stamped footprint back out. */
function evictFromFootprint(world, b) {
  world.queryRadius(b.x, b.y, Math.max(b.w, b.h) / 2 + 1.5, _near);
  for (const e of _near) {
    if (e.kind !== 'unit' || !e.alive || e.flying) continue;
    if (e.x <= b.tx || e.x >= b.tx + b.w || e.y <= b.ty || e.y >= b.ty + b.h) continue;
    faceApproach(b, e, e.radius + BUILD_PAD, _pt);
    const t = world.grid.nearestPassable(_pt.x, _pt.y);
    e.x = t.x + 0.5; e.y = t.y + 0.5;
    e.vx = 0; e.vy = 0;
    e.path = null; e.pathGoal = null; e.stuckTicks = 0;
  }
}

function abandonBuild(world, u) {
  u.buildTargetId = null;
  if (u.orderQueue.length) { nextOrder(u); return; }
  setOrder(u, { type: 'gather', targetId: null }, false);
}

function finishBuild(world, u) {
  // Deliberate deviation from BW: the builder walks itself back to the mineral
  // line instead of standing idle next to the finished structure. It matters a
  // lot for AI sanity and nobody has ever wanted the other behaviour.
  if (u.orderQueue.length) { nextOrder(u); return; }
  setOrder(u, { type: 'gather', targetId: null }, false);
}

// ---- repair --------------------------------------------------------------
function tickRepair(world, u, o) {
  const t = world.alive(o.targetId);
  if (!t || t.hp >= t.maxHp || t.owner !== u.owner) { nextOrder(u); return; }
  if (edgeDist(t, u.x, u.y) > u.radius + BUILD_REACH) {
    u.moveTarget = approachTarget(world, u, t, BUILD_PAD);
    return;
  }
  u.moveTarget = null;
  const pl = world.players[u.owner];
  const def = t.kind === 'building' ? BuildingDefs[t.defId] : UnitDefs[t.defId];
  const rate = t.maxHp / def.buildTime;
  const heal = rate * TICK_DT;
  const costM = (def.cost.minerals || 0) * (heal / t.maxHp) * 0.6;
  const costG = (def.cost.gas || 0) * (heal / t.maxHp) * 0.6;
  if (pl.minerals < costM || pl.gas < costG) return;
  pl.minerals -= costM;
  pl.gas -= costG;
  t.hp = Math.min(t.maxHp, t.hp + heal);
}
